import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { principles, site, stats, testimonials } from "@/mocks/site";

const ORIGIN = "https://bebfdsx.cn";

const milestones = [
  {
    year: "起步",
    title: "从一份个人速览笔记开始",
    desc: "最初只是把每天刷到的话题整理成三条要点发给朋友，收到的第一条反馈是『这样省时间』。",
  },
  {
    year: "成型",
    title: "固定成六个专题方向",
    desc: "把内容按综艺、影视、舞台、造型、音乐、热梗分成六类，方便按兴趣直接进入，而不是一路往下翻。",
  },
  {
    year: "现在",
    title: "两次更新的热榜节奏",
    desc: "每天上午与傍晚各更新一次热榜，重大话题当天加更，保持话题的时效感。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 黑料在线的定位与内容原则｜黑料在线",
    description:
      "了解黑料在线的品牌定位、发展理念与内容原则。我们只做公开讨论的二次归纳，重点写在前三行，隐私优先、秒开无跳转，倡导理性吃瓜。",
    keywords: "黑料在线关于我们,黑料在线定位,娱乐速览内容原则",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        url: `${ORIGIN}/about`,
        name: "关于我们",
        inLanguage: "zh-CN",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "关于我们", item: `${ORIGIN}/about` },
          ],
        },
        about: {
          "@type": "Organization",
          name: "黑料在线",
          url: `${ORIGIN}/`,
          email: site.email,
          description: site.intro,
        },
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 max-w-3xl font-heading text-2xl font-black leading-tight tracking-tight text-foreground-950 md:text-4xl">
            我们想做的，是让吃瓜这件事更省时间也更体面
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">{site.intro}</p>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <div className="relative w-full overflow-hidden rounded-card" style={{ aspectRatio: "4 / 3" }}>
              <img
                src="https://readdy.ai/api/search-image?query=warm%20modern%20editorial%20workspace%20with%20notebooks%2C%20coffee%20and%20laptop%20on%20wooden%20desk%2C%20amber%20natural%20light%2C%20clean%20aesthetic%2C%20magazine%20style%20photography&width=1200&height=900&seq=about-hl-01&orientation=landscape"
                alt="温暖的编辑工作台，代表黑料在线的内容整理场景"
                title="黑料在线 · 编辑工作台"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-label text-primary-700">
                <i className="ri-compass-3-line" />
                网站定位
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                娱乐话题的速览层，而不是又一个链接列表
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                大多数话题聚合页只负责把链接堆在一起，读者需要自己判断哪条重要。我们反过来做：先给出结论，再补充脉络，把篇幅控制在 90 秒能读完的长度。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                我们不做爆料、不做站队，只呈现公开舞台上已经发生的事，以及大家对它的讨论方式。这样即便只是随手刷两分钟，也不会被情绪带着走。
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/topics"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-layout-grid-line" />
                  看看专题库
                </Link>
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-label text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                >
                  <i className="ri-mail-send-line" />
                  联系我们
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-card border border-background-200 bg-background-50 p-5">
                <p className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">{s.value}</p>
                <p className="mt-1 text-sm text-foreground-600">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展理念</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            没有宏大的目标，只是把一件小事做得越来越顺手。
          </p>
          <ol className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {milestones.map((m) => (
              <li key={m.year} className="relative rounded-card border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="inline-flex rounded-full bg-accent-100 px-3 py-1 text-xs font-label text-accent-900">
                  {m.year}
                </span>
                <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{m.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="w-full bg-secondary-50 px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这四条是我们判断一条内容要不要做的标准。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item, index) => (
              <article key={item.title} className="flex gap-4 rounded-card border border-background-200 bg-background-50 p-5">
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-lg ${
                    index % 2 === 0 ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-800"
                  }`}
                >
                  <i className={item.icon} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">用户口碑</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <blockquote key={t.name} className="rounded-card border border-background-200 bg-background-50 p-5 md:p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-400" />
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{t.text}</p>
                <footer className="mt-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-semibold text-secondary-900">
                    {t.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block font-heading text-sm font-semibold text-foreground-950">{t.name}</span>
                    <span className="block text-xs text-foreground-500">{t.role}</span>
                  </span>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        <section className="w-full px-4 pb-16 md:px-8">
          <div className="flex flex-col items-start justify-between gap-6 rounded-card border border-primary-200 bg-primary-50 p-6 md:flex-row md:items-center md:p-8">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                合作、投稿或内容纠错，都可以直接发邮件给我们。
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={`mailto:${site.email}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-5 py-3 text-sm font-label text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
              >
                <i className="ri-mail-line" />
                {site.email}
              </a>
              <Link
                to="/contact"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                前往留言页面
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}